import { type ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';
import { ActivityIndicator, Divider, List, Text, TouchableRipple } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { AppBottomSheet, BottomSheetScrollView, NotificationDot } from '@/components/atoms';
import { rippleOnSurface, useAppTheme } from '@/theme';

const STATUS_PICKER_ROW_HEIGHT = 57;
const STATUS_DOT_SIZE = 12;

interface StatusOption {
  readonly id: number;
  readonly label: string;
  readonly color: string | null;
}

interface StatusPickerSheetProps {
  readonly visible: boolean;
  readonly title: string;
  readonly clearLabel: string;
  readonly currentStatusId: number | null | undefined;
  readonly statuses: readonly StatusOption[];
  readonly loading: boolean;
  readonly onSelect: (statusId: number | null) => void;
  readonly onDismiss: () => void;
}

const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  dotContainer: {
    justifyContent: 'center',
  },
  row: {
    justifyContent: 'center',
    minHeight: STATUS_PICKER_ROW_HEIGHT,
    width: '100%',
  },
  touchable: {
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    width: '100%',
  },
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: STATUS_PICKER_ROW_HEIGHT,
    paddingHorizontal: 16,
  },
  title: {
    flex: 1,
    fontSize: 16,
    lineHeight: 24,
  },
});

interface StatusLeftIconProps {
  readonly color: string | null;
  readonly testID: string;
}

const StatusLeftIcon = ({ color, testID }: StatusLeftIconProps) =>
  color ? (
    <View style={styles.dotContainer} testID={testID}>
      <NotificationDot color={color} size={STATUS_DOT_SIZE} />
    </View>
  ) : null;

interface SelectedCheckIconProps {
  readonly testID: string;
}

const SelectedCheckIcon = ({ testID }: SelectedCheckIconProps) => {
  const { colors } = useAppTheme();

  return (
    <View testID={testID}>
      <List.Icon icon="check" color={colors.primary} />
    </View>
  );
};

interface StatusLabelIconProps {
  readonly icon: string;
  readonly testID: string;
}

const StatusLabelIcon = ({ icon, testID }: StatusLabelIconProps) => (
  <View testID={testID}>
    <List.Icon icon={icon} />
  </View>
);

interface SheetItemProps {
  readonly title: string;
  readonly selected: boolean;
  readonly onPress: () => void;
  readonly testID: string;
  readonly selectedIconTestID: string;
  readonly selectedBackgroundColor: string;
  readonly leftIcon: ReactNode;
  readonly showDivider?: boolean;
}

const SheetItem = ({
  title,
  selected,
  onPress,
  testID,
  selectedIconTestID,
  selectedBackgroundColor,
  leftIcon,
  showDivider = false,
}: SheetItemProps) => {
  const { colors } = useAppTheme();

  return (
    <View style={styles.row}>
      <TouchableRipple
        accessibilityLabel={title}
        accessibilityRole="button"
        onPress={onPress}
        rippleColor={rippleOnSurface}
        style={[
          styles.touchable,
          selected ? { backgroundColor: selectedBackgroundColor } : undefined,
        ]}
        testID={testID}>
        <View style={styles.content} testID={`${testID}-content`}>
          {leftIcon}
          <Text numberOfLines={1} style={[styles.title, { color: colors.onSurface }]}>
            {title}
          </Text>
          {selected ? <SelectedCheckIcon testID={selectedIconTestID} /> : null}
        </View>
      </TouchableRipple>
      {showDivider ? <Divider /> : null}
    </View>
  );
};

export const StatusPickerSheet = ({
  visible,
  title,
  clearLabel,
  currentStatusId,
  statuses,
  loading,
  onSelect,
  onDismiss,
}: StatusPickerSheetProps) => {
  const { colors } = useAppTheme();
  const insets = useSafeAreaInsets();

  const isNoneSelected = currentStatusId == null;

  return (
    <AppBottomSheet visible={visible} snapPoints={['50%', '90%']} onDismiss={onDismiss}>
      <View style={styles.header}>
        <Text variant="titleMedium">{title}</Text>
      </View>
      <Divider />
      {loading ? (
        <ActivityIndicator size="small" style={{ padding: 32 }} />
      ) : (
        <BottomSheetScrollView contentContainerStyle={{ paddingBottom: insets.bottom }}>
          <SheetItem
            key="clear-status"
            leftIcon={
              <StatusLabelIcon
                icon="close-circle-outline"
                testID="status-picker-sheet-item-clear-left-icon"
              />
            }
            onPress={() => onSelect(null)}
            selected={isNoneSelected}
            selectedBackgroundColor={colors.secondaryContainer}
            selectedIconTestID="status-picker-sheet-selected-icon-clear"
            showDivider={true}
            testID="status-picker-sheet-item-clear"
            title={clearLabel}
          />
          {statuses.map((status) => {
            const isSelected = status.id === currentStatusId;

            return (
              <SheetItem
                key={status.id}
                leftIcon={
                  <StatusLeftIcon
                    color={status.color}
                    testID={`status-picker-sheet-item-${status.id}-left-icon`}
                  />
                }
                onPress={() => onSelect(status.id)}
                selected={isSelected}
                selectedBackgroundColor={colors.secondaryContainer}
                selectedIconTestID={`status-picker-sheet-selected-icon-${status.id}`}
                testID={`status-picker-sheet-item-${status.id}`}
                title={status.label}
              />
            );
          })}
        </BottomSheetScrollView>
      )}
    </AppBottomSheet>
  );
};
